import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ProjectStatus } from './project-types';
import type { CardCommentData, CardData, ProjectData } from '@/types/api';

export function useMyProjects(page = 1) {
  return useQuery({
    queryKey: ['projects', 'mine', page],
    queryFn: async (): Promise<Paginated<ProjectData>> => {
      const { data } = await api.get<Paginated<ProjectData>>('/v1/projects', { params: { page } });
      return data;
    },
  });
}

export function useClientProjects(clientId: number | undefined, page = 1) {
  return useQuery({
    queryKey: clientId == null ? ['projects', 'client', null] : ['projects', 'client', clientId, page],
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<ProjectData>> => {
      const { data } = await api.get<Paginated<ProjectData>>(
        `/v1/clients/${clientId}/projects`,
        { params: { page } },
      );
      return data;
    },
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: id == null ? ['project', null] : ['project', id],
    enabled: id != null,
    queryFn: async (): Promise<ProjectData> => {
      const { data } = await api.get<ProjectData>(`/v1/projects/${id}`);
      return data;
    },
  });
}

export function useUploadCardAttachment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      await primeCsrf();
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/v1/cards/${cardId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards'] }),
  });
}

export function useDeleteCardAttachment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: number) => {
      await primeCsrf();
      await api.delete(`/v1/cards/${cardId}/attachments/${mediaId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards'] }),
  });
}

export function useUpdateProject(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      name?: string;
      description?: string | null;
      type?: string;
      status?: ProjectStatus;
    }): Promise<ProjectData> => {
      await primeCsrf();
      const { data } = await api.patch<ProjectData>(`/v1/projects/${projectId}`, patch);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

/**
 * Set a project's status from a list, where the project id isn't known until
 * the row is acted on (unlike `useUpdateProject`, which binds one id).
 */
export function useSetProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: ProjectStatus }): Promise<ProjectData> => {
      await primeCsrf();
      const { data } = await api.patch<ProjectData>(`/v1/projects/${id}`, { status });
      return data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/projects/${projectId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useCreateProject(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string; type: string }): Promise<ProjectData> => {
      await primeCsrf();
      const { data } = await api.post<ProjectData>(`/v1/clients/${clientId}/projects`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
    },
  });
}

export function useCards(projectId: number | undefined) {
  return useQuery({
    queryKey: projectId == null ? ['cards', null] : ['cards', projectId],
    enabled: projectId != null,
    queryFn: async (): Promise<CardData[]> => {
      const { data } = await api.get<CardData[]>(`/v1/projects/${projectId}/cards`);
      return data;
    },
  });
}

export function useCreateCard(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      body?: string;
      status?: string;
      due_date?: string | null;
    }): Promise<CardData> => {
      await primeCsrf();
      const { data } = await api.post<CardData>(`/v1/projects/${projectId}/cards`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards', projectId] }),
  });
}

/**
 * Delete a card outright, with its comments and attachments. Admin only on the
 * server, and there is no trash: cards hard-delete.
 */
/** Remove a comment. Yours always; an admin's reach extends to anyone's. */
export function useDeleteComment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/cards/${cardId}/comments/${commentId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['card-comments', cardId] }),
  });
}

export function useDeleteCard(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cardId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/cards/${cardId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards', projectId] }),
  });
}

export function useUpdateCard(projectId: number) {
  const qc = useQueryClient();
  const key = ['cards', projectId] as const;

  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<{ title: string; body: string; status: string; position: number; due_date: string | null }>;
    }): Promise<CardData> => {
      await primeCsrf();
      const { data } = await api.patch<CardData>(`/v1/cards/${id}`, patch);
      return data;
    },
    // Optimistic update so kanban drags feel instant and rapid drags don't race.
    // Snapshot the previous list; on error, roll it back; on settle, refetch.
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<CardData[]>(key);
      if (previous) {
        qc.setQueryData<CardData[]>(
          key,
          previous.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useCardComments(cardId: number | undefined) {
  return useQuery({
    queryKey: cardId == null ? ['card-comments', null] : ['card-comments', cardId],
    enabled: cardId != null,
    queryFn: async (): Promise<CardCommentData[]> => {
      const { data } = await api.get<CardCommentData[]>(`/v1/cards/${cardId}/comments`);
      return data;
    },
  });
}

export function useAddComment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string): Promise<CardCommentData> => {
      await primeCsrf();
      const { data } = await api.post<CardCommentData>(`/v1/cards/${cardId}/comments`, { body });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['card-comments', cardId] }),
  });
}
